import { UserRoundCog } from "lucide-react";

import { Table } from "../../../components/ui/Table";
import { CustomerDetailsController } from "../../../services/controllers/customer";
import { formatDateShort, formatTutorName } from "../../../services/functions";

import { resolveCategoryActivity } from "./categoryActivity";
import { PetRowActions } from "./PetRowActions";

const membershipColors = {
  ABSENT: "bg-orange-50 text-orange-700 border-orange-200",
  NO_PRODUCT: "bg-blue-50 text-blue-700 border-blue-200",
  NO_ADDITIONAL: "bg-violet-50 text-violet-700 border-violet-200",
  POTENTIAL_SUBSCRIBER: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function StatusBadge({ active, activeLabel = "Sim", inactiveLabel = "Não" }) {
  return (
    <span className={`inline-flex w-auto rounded-full border px-2 py-0.5 text-[11px] ${active
      ? "border-orange-200 bg-orange-50 text-scale-600"
      : "border-zinc-200 bg-stone-50 text-gray-600"
    }`}>
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

function AbsenceBadge({ days }) {
  if (days == null) return <span>—</span>;
  const tone = days <= 14
    ? "bg-emerald-50 text-emerald-700"
    : days <= 30
      ? "bg-orange-50 text-orange-700"
      : "bg-red-50 text-red-600";
  return <span className={`inline-flex w-auto rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{days}d</span>;
}

function DateCell({ value, empty = "—" }) {
  return <span className="text-xs text-gray-600">{value ? formatDateShort(value) : empty}</span>;
}

function CustomerCell({ customer }) {
  const CCustomerDetails = CustomerDetailsController();
  return (
    <button
      type="button"
      className="flex items-center gap-1 border-b border-transparent bg-transparent font-medium text-gray-800 hover:border-gray-700"
      disabled={!customer.customerId}
      onClick={() => customer.customerId && CCustomerDetails.open(customer.customerId)}
    >
      <UserRoundCog aria-hidden="true" size={15} />
      {formatTutorName(customer.customerName)}
    </button>
  );
}

function PetCell({ pet }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className={pet.blocked ? "text-gray-400 line-through" : "font-medium text-gray-700"}>
        {pet.petName || "Sem nome"}
      </span>
      {pet.blocked && <span className="text-[10px] text-red-500">Bloqueado</span>}
    </span>
  );
}

function OpportunitiesCell({ memberships }) {
  if (!memberships.length) return <span className="text-gray-400">—</span>;
  return (
    <span className="flex flex-wrap gap-1 py-1">
      {memberships.map((membership) => (
        <span
          key={membership.membershipId || `${membership.ruleCode}-${membership.subtypeCode}`}
          title={membership.label}
          className={`inline-flex w-auto rounded-full border px-2 py-1 text-[10px] font-semibold ${membershipColors[membership.ruleCode] || "border-gray-200 bg-gray-50 text-gray-700"}`}
        >
          {membership.ruleLabel}
        </span>
      ))}
    </span>
  );
}

function ContactCell({ contactSummary }) {
  const contacted = contactSummary.status === "CONTACTED";
  return (
    <span className="flex flex-col gap-0.5 text-xs">
      <span className={contacted ? "font-semibold text-emerald-600" : "text-gray-500"}>
        {contacted ? "Realizado" : "Pendente"}
      </span>
      {contacted && contactSummary.lastContactAt && (
        <span className="text-[10px] text-gray-400">{formatDateShort(contactSummary.lastContactAt)}</span>
      )}
    </span>
  );
}

function renderCell(column, opportunity) {
  const { activity, categoryActivity, contactSummary, memberships, pet, subscription } = opportunity;

  switch (column.key) {
    case "customer": return <CustomerCell customer={opportunity} />;
    case "pet": return <PetCell pet={pet} />;
    case "subscriber": return subscription.status ? <StatusBadge active={subscription.status === "ACTIVE"} /> : <span>—</span>;
    case "formerSubscriber": return subscription.status ? <StatusBadge active={subscription.status === "FORMER"} /> : <span>—</span>;
    case "frequency": return subscription.frequencyPerMonth != null ? <span className="text-xs text-sky-600">{subscription.frequencyPerMonth}x/mês</span> : <span>—</span>;
    case "absence": return <AbsenceBadge days={activity.absenceDays} />;
    case "lastSubscription": return <DateCell value={subscription.lastSubscriptionAt} />;
    case "lastAppointment": return <DateCell value={activity.lastAppointmentAt} />;
    case "nextAppointment": return <DateCell value={activity.nextAppointmentAt} empty="Sem agenda" />;
    case "opportunities": return <OpportunitiesCell memberships={memberships} />;
    case "contact": return <ContactCell contactSummary={contactSummary} />;
    case "actions": return <PetRowActions customer={opportunity} />;
    default:
      if (column.categoryCode) {
        const category = resolveCategoryActivity(categoryActivity, column.categoryCode);
        return <DateCell value={category.value} empty={category.empty} />;
      }
      return <span>—</span>;
  }
}

export function PetRow({ customer, columns }) {
  return (
    <Table.Line>
      {columns.map((column) => (
        <Table.Item key={column.key}>{renderCell(column, customer)}</Table.Item>
      ))}
    </Table.Line>
  );
}
